import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Avatar,
  Chip,
  Divider,
  LinearProgress,
} from "@mui/material";
import {
  Face2Outlined,
  LibraryMusicOutlined,
  LyricsOutlined,
  TokenOutlined,
  TrendingUpOutlined,
  CheckCircleOutline,
  ErrorOutline,
  AccessTimeOutlined,
  VideocamOutlined,
  VideoLibraryOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  ArrowForwardOutlined,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { useCustomTheme } from "../../utils/useCustomTheme";
import { UserContext } from "../../context/UserContext";
import moment from "moment";
import PageHeader from "../../common/PageHeader";
import { InlineLoader } from "../../LoadingComp";
import { mediaSrc } from "../../utils/mediaSrc";

// ─────────────────────────────────────────────────────────
// Stat Card
// ─────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, color, config }) => (
  <Box
    sx={{
      p: 2.5,
      borderRadius: `${config.card.borderRadius}px`,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "background.paper",
      display: "flex",
      alignItems: "flex-start",
      gap: 2,
      transition: "box-shadow 0.2s ease, transform 0.2s ease",
      "&:hover": { boxShadow: 4, transform: "translateY(-2px)" },
    }}
  >
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: `${config.card.borderRadius / 1.5}px`,
        bgcolor: `${color}.main`,
        opacity: 0.12,
        position: "absolute",
      }}
    />
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: `${config.card.borderRadius / 1.5}px`,
        border: "1px solid",
        borderColor: `${color}.main`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        position: "relative",
      }}
    >
      <Icon sx={{ fontSize: "1.3rem", color: `${color}.main` }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontFamily={config.font_family}
      >
        {label}
      </Typography>
      <Typography
        variant="h5"
        fontWeight={config.font_weight_bold}
        fontFamily={config.font_family}
        lineHeight={1.2}
      >
        {value ?? "—"}
      </Typography>
      {sub && (
        <Typography
          variant="caption"
          color="text.disabled"
          fontFamily={config.font_family}
        >
          {sub}
        </Typography>
      )}
    </Box>
  </Box>
);

const QuickActionCard = ({ icon: Icon, label, hint, color, pageId, config }) => (
  <Box
    component="button"
    type="button"
    onClick={() => navigateToPage(pageId)}
    sx={{
      p: 2,
      borderRadius: `${config.card.borderRadius}px`,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "background.paper",
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      width: "100%",
      textAlign: "left",
      cursor: "pointer",
      transition: "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease",
      "&:hover": {
        boxShadow: 3,
        transform: "translateY(-1px)",
        borderColor: `${color}.main`,
      },
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: `${config.card.borderRadius / 1.5}px`,
        bgcolor: `${color}.main`,
        opacity: 0.12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        position: "relative",
      }}
    >
      <Icon sx={{ fontSize: "1.2rem", color: `${color}.main`, position: "relative" }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        fontWeight={config.font_weight_semibold}
        fontFamily={config.font_family}
        noWrap
      >
        {label}
      </Typography>
      {hint && (
        <Typography
          variant="caption"
          color="text.secondary"
          fontFamily={config.font_family}
          noWrap
          display="block"
        >
          {hint}
        </Typography>
      )}
    </Box>
    <ArrowForwardOutlined sx={{ fontSize: "1rem", color: "text.disabled", flexShrink: 0 }} />
  </Box>
);

// ─────────────────────────────────────────────────────────
// Mini bar chart (pure CSS)
// ─────────────────────────────────────────────────────────
const MiniBarChart = ({ data, config }) => {
  const max = Math.max(...data.map((d) => Number(d.credits_spent || 0)), 1);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: 0.8,
        height: 64,
        mt: 1,
      }}
    >
      {data.map((d, i) => {
        const pct = (Number(d.credits_spent) / max) * 100;
        return (
          <Box
            key={i}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.4,
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: `${Math.max(pct, 6)}%`,
                bgcolor: "primary.main",
                borderRadius: "4px 4px 0 0",
                opacity: 0.75,
                transition: "height 0.4s ease",
                minHeight: 4,
              }}
            />
            <Typography
              sx={{
                fontSize: "0.55rem",
                color: "text.disabled",
                fontFamily: config.font_family,
              }}
            >
              {moment(d.date).format("DD")}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────
// Task label map
// ─────────────────────────────────────────────────────────
const TASK_LABELS = {
  inf_maker: "Character Create",
  inf_var_maker: "Character Variation",
  product_showcase_maker: "Product Ad",
  video_agent_generation: "Video Agent",
  video_studio_generation: "Video Studio",
  music_generation: "Music Generation",
  music_cover_generation: "Music Cover",
  lyrics_generation: "Lyrics Generation",
};

function navigateToPage(pageId) {
  const params = new URLSearchParams(window.location.search);
  params.set("page", pageId);
  params.delete("checkout");
  window.history.pushState({}, "", `${window.location.pathname}?${params}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

const taskLabel = (task) => TASK_LABELS[task] || task;

function activityUiStatus(raw) {
  const s = String(raw ?? "").toLowerCase();
  if (["completed", "ready", "success"].includes(s)) return "success";
  if (["failed", "error"].includes(s)) return "error";
  return "pending";
}

function activityTimestamp(item) {
  return (
    item.updated_at ??
    item.updatedAt ??
    item.created_at ??
    item.createdAt ??
    null
  );
}

/** Build latest-5 activity from list APIs when dashboard payload has none. */
async function fetchRecentActivity(hitAxios) {
  const quiet = { showLoading: false, showSnackbar: false, post: false, admin: false };
  try {
    const [adsRes, songsRes, lyricsRes, videosRes] = await Promise.all([
      hitAxios({ path: "/api/product-ads", ...quiet }),
      hitAxios({ path: "/api/music/songs/jobs?limit=10", ...quiet }),
      hitAxios({ path: "/api/music/lyrics/jobs?limit=10", ...quiet }),
      hitAxios({ path: "/api/video-studio/jobs?limit=10", ...quiet }),
    ]);

    const ads = adsRes?.data?.data?.ads ?? [];
    const songs = songsRes?.data?.data?.jobs ?? [];
    const lyrics = lyricsRes?.data?.data?.jobs ?? [];
    const videos = videosRes?.data?.data?.jobs ?? [];

    const items = [
      ...videos.map((job) => ({
        task: "video_studio_generation",
        credits: job.charged_credits ?? job.chargedCredits ?? null,
        status: activityUiStatus(job.status),
        des: (job.title || "Video").trim(),
        createdAt: activityTimestamp(job),
      })),
      ...ads.map((ad) => ({
        task: "product_showcase_maker",
        credits: ad.charged_credits ?? ad.chargedCredits ?? null,
        status: activityUiStatus(ad.status),
        des: (ad.title || ad.product_name || ad.productName || "Product ad").trim(),
        createdAt: activityTimestamp(ad),
      })),
      ...songs.map((song) => ({
        task: "music_generation",
        credits: song.charged_credits ?? song.chargedCredits ?? null,
        status: activityUiStatus(song.status),
        des: (song.title || song.style || "Music track").trim(),
        createdAt: activityTimestamp(song),
      })),
      ...lyrics.map((lyric) => ({
        task: "lyrics_generation",
        credits: lyric.charged_credits ?? lyric.chargedCredits ?? null,
        status: activityUiStatus(lyric.status),
        des: (lyric.title || "Lyrics").trim(),
        createdAt: activityTimestamp(lyric),
      })),
    ].filter((i) => i.createdAt);

    items.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    return items.slice(0, 5);
  } catch (err) {
    console.error("Recent activity fallback failed:", err);
    return [];
  }
}

/** Merge API stats; tolerate legacy get_dashboard (gallery/showcase) shapes. */
function normalizeDashboardStats(raw) {
  const s = raw && typeof raw === "object" ? raw : {};
  return {
    influencers: {
      total: Number(s.influencers?.total ?? 0),
      active: Number(s.influencers?.active ?? 0),
    },
    product_ads: {
      total: Number(s.product_ads?.total ?? s.showcase?.total ?? 0),
      completed: Number(s.product_ads?.completed ?? s.showcase?.success ?? 0),
    },
    video_studio: {
      total: Number(s.video_studio?.total ?? 0),
      completed: Number(s.video_studio?.completed ?? 0),
    },
    music: {
      total: Number(s.music?.total ?? 0),
      completed: Number(s.music?.completed ?? 0),
    },
    lyrics: {
      total: Number(s.lyrics?.total ?? 0),
      ready: Number(s.lyrics?.ready ?? 0),
    },
  };
}

async function fetchCreationCounts(hitAxios) {
  const quiet = { showLoading: false, showSnackbar: false, post: false, admin: false };
  try {
    const [adsRes, songsRes, lyricsRes, videosRes] = await Promise.all([
      hitAxios({ path: "/api/product-ads", ...quiet }),
      hitAxios({ path: "/api/music/songs/jobs?limit=50", ...quiet }),
      hitAxios({ path: "/api/music/lyrics/jobs?limit=50", ...quiet }),
      hitAxios({ path: "/api/video-studio/jobs?limit=50", ...quiet }),
    ]);

    const ads = adsRes?.data?.data?.ads ?? [];
    const songs = songsRes?.data?.data?.jobs ?? [];
    const lyrics = lyricsRes?.data?.data?.jobs ?? [];
    const videos = videosRes?.data?.data?.jobs ?? [];

    return {
      video_studio: {
        total: Number(videosRes?.data?.data?.pagination?.total ?? videos.length),
        completed: videos.filter((j) => j.status === "completed").length,
      },
      product_ads: {
        total: ads.length,
        completed: ads.filter((a) => a.status === "completed").length,
      },
      music: {
        total: Number(songsRes?.data?.data?.pagination?.total ?? songs.length),
        completed: songs.filter((j) => j.status === "completed").length,
      },
      lyrics: {
        total: Number(lyricsRes?.data?.data?.pagination?.total ?? lyrics.length),
        ready: lyrics.filter((j) => j.status === "ready").length,
      },
    };
  } catch (err) {
    console.error("Dashboard count fallback failed:", err);
    return {
      video_studio: { total: 0, completed: 0 },
      product_ads: { total: 0, completed: 0 },
      music: { total: 0, completed: 0 },
      lyrics: { total: 0, ready: 0 },
    };
  }
}

// ─────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────
const Dash = ({ lang }) => {
  const { hitAxios } = useContext(GlobalContext);
  const { userData } = useContext(UserContext);
  const { config } = useCustomTheme();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function getDashboard() {
    setLoading(true);
    try {
      const res = await hitAxios({
        path: "/api/user/get_dashboard",
        post: false,
        admin: false,
        showLoading: false,
        showSnackbar: false,
      });
      const body = res?.data ?? res;
      if (!body?.success || !body?.data) return;

      let stats = normalizeDashboardStats(body.data.stats);

      // Stale backend may omit video_studio — load live counts from list APIs.
      if (body.data.stats?.video_studio == null) {
        const live = await fetchCreationCounts(hitAxios);
        stats = {
          ...stats,
          video_studio: live.video_studio,
          product_ads: live.product_ads,
          music: live.music,
          lyrics: live.lyrics,
        };
      } else if (body.data.stats?.product_ads == null) {
        const live = await fetchCreationCounts(hitAxios);
        stats = {
          ...stats,
          product_ads: live.product_ads,
          music: live.music,
          lyrics: live.lyrics,
        };
      }

      let usage_logs = Array.isArray(body.data.usage_logs) ? body.data.usage_logs : [];
      if (usage_logs.length === 0) {
        usage_logs = await fetchRecentActivity(hitAxios);
      }

      setData({ ...body.data, stats, usage_logs });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getDashboard();
  }, []);

  // ── Plan parse ──
  const plan = useMemo(() => {
    if (!userData?.plan) return null;
    try {
      return typeof userData.plan === "string"
        ? JSON.parse(userData.plan)
        : userData.plan;
    } catch {
      return null;
    }
  }, [userData?.plan]);

  const credits = Number(userData?.credits || 0);
  const maxCredits = Number(plan?.credits || 0);
  const creditPct =
    maxCredits > 0 ? Math.min((credits / maxCredits) * 100, 100) : 0;

  const expiryDate = userData?.plan_ending
    ? moment(userData.plan_ending)
    : null;
  const daysLeft = expiryDate ? expiryDate.diff(moment(), "days") : null;
  const isExpired = daysLeft !== null && daysLeft < 0;
  const isExpiringSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 5;

  const creditBarColor =
    creditPct > 50 ? "success" : creditPct > 20 ? "warning" : "error";

  if (loading) {
    return <InlineLoader message={lang?.dashLoading || "Loading dashboard"} />;
  }

  if (!data) {
    return (
      <Box sx={{ textAlign: "center", py: 10 }}>
        <Typography color="text.secondary">
          {lang?.dashLoadError || "Failed to load dashboard"}
        </Typography>
      </Box>
    );
  }

  const stats = normalizeDashboardStats(data.stats);
  const {
    credit_chart = [],
    task_breakdown = [],
    recent_influencers = [],
    usage_logs = [],
  } = data;
  const recentActivity = usage_logs.slice(0, 5);

  return (
    <Box>
      <Box mb={2}>
        <PageHeader
          icon={DashboardOutlined}
          title={lang?.dashboard || "Dashboard"}
          subtitle={`${lang?.welcomeBack || "Welcome back"}, ${userData?.name || "—"} · ${moment().format("dddd, MMMM D YYYY")}`}
        />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* ── Stat Cards ── */}
        <Grid container spacing={2}>
          {[
            {
              icon: VideocamOutlined,
              label: lang?.dashVideos || "Videos",
              value: stats.video_studio.total,
              sub: `${stats.video_studio.completed} ${lang?.completed || "completed"}`,
              color: "warning",
            },
            {
              icon: Face2Outlined,
              label: lang?.imageStudioNav || "Image Studio",
              value: stats.influencers.total,
              sub: `${stats.influencers.active} ${lang?.active || "active"}`,
              color: "primary",
            },
            {
              icon: LibraryMusicOutlined,
              label: lang?.musicNav || "Music",
              value: stats.music.total,
              sub: `${stats.music.completed} ${lang?.completed || "completed"}`,
              color: "info",
            },
            {
              icon: LyricsOutlined,
              label: lang?.dashLyrics || "Lyrics",
              value: stats.lyrics.total,
              sub: `${stats.lyrics.ready} ${lang?.ready || "ready"}`,
              color: "success",
            },
          ].map((card) => (
            <Grid item xs={12} sm={6} lg={3} key={card.label}>
              <StatCard {...card} config={config} />
            </Grid>
          ))}
        </Grid>

        {/* ── Quick Actions ── */}
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight={config.font_weight_bold}
            fontFamily={config.font_family}
            mb={1.5}
          >
            {lang?.dashQuickActions || "Quick actions"}
          </Typography>
          <Grid container spacing={2}>
            {[
              {
                icon: VideocamOutlined,
                label: lang?.videoStudioNav || "Video Studio",
                hint: lang?.dashCreateVideo || "Create a new AI video",
                color: "warning",
                pageId: "video-studio",
              },
              {
                icon: VideoLibraryOutlined,
                label: lang?.videoLibraryNav || "Video Library",
                hint: lang?.dashOpenLibrary || "Browse your video library",
                color: "secondary",
                pageId: "video-library",
              },
              {
                icon: Face2Outlined,
                label: lang?.imageStudioNav || "Image Studio",
                hint: lang?.dashManageCharacters || "Create and manage AI characters",
                color: "primary",
                pageId: "influencers",
              },
              {
                icon: LibraryMusicOutlined,
                label: lang?.musicNav || "Music",
                hint: lang?.dashOpenMusic || "Generate music tracks",
                color: "info",
                pageId: "music",
              },
              {
                icon: CreditCardOutlined,
                label: lang?.billingNav || "Billing",
                hint: lang?.dashOpenBilling || "Plans and credits",
                color: "success",
                pageId: "billing",
              },
            ].map((action) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={action.pageId}>
                <QuickActionCard {...action} config={config} />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ── Plan + Credits + Chart row ── */}
        <Grid container spacing={2}>
          {/* Plan card */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: `${config.card.borderRadius}px`,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={config.font_weight_bold}
                fontFamily={config.font_family}
              >
                {lang?.yourPlan || "Your Plan"}
              </Typography>

              {plan ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={config.font_weight_bold}
                      fontFamily={config.font_family}
                      color="primary"
                    >
                      {plan.title}
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        isExpired
                          ? lang?.expired || "Expired"
                          : isExpiringSoon
                            ? `${daysLeft}d left`
                            : expiryDate?.format("MMM D")
                      }
                      color={
                        isExpired
                          ? "error"
                          : isExpiringSoon
                            ? "warning"
                            : "success"
                      }
                      variant="outlined"
                      sx={{ fontWeight: 700, fontSize: "0.68rem" }}
                    />
                  </Box>

                  {/* Credits bar */}
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.8,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <TokenOutlined
                          sx={{ fontSize: "0.85rem", color: "text.disabled" }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontFamily={config.font_family}
                        >
                          {lang?.credits || "Credits"}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        fontFamily={config.font_family}
                      >
                        {credits.toLocaleString()}
                        {maxCredits > 0 && (
                          <Box component="span" sx={{ color: "text.disabled" }}>
                            {" "}
                            / {maxCredits.toLocaleString()}
                          </Box>
                        )}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={creditPct}
                      color={creditBarColor}
                      sx={{
                        height: 6,
                        borderRadius: 99,
                        bgcolor: `${creditBarColor}.main`,
                        opacity: 1,
                        "& .MuiLinearProgress-bar": { borderRadius: 99 },
                      }}
                    />
                  </Box>

                  {/* Expiry */}
                  {expiryDate && !isExpired && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <AccessTimeOutlined
                        sx={{ fontSize: "0.8rem", color: "text.disabled" }}
                      />
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        fontFamily={config.font_family}
                      >
                        {lang?.renewsOn || "Renews"}{" "}
                        {expiryDate.format("MMM D, YYYY")} ·{" "}
                        {expiryDate.fromNow()}
                      </Typography>
                    </Box>
                  )}

                  {isExpired && (
                    <Typography
                      variant="caption"
                      color="error.main"
                      fontWeight={700}
                    >
                      {lang?.planExpired || "Your plan has expired"}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontFamily={config.font_family}
                >
                  {lang?.noPlan || "No active plan"}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Credit chart */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: `${config.card.borderRadius}px`,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                height: "100%",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <TrendingUpOutlined
                  sx={{ fontSize: "1rem", color: "primary.main" }}
                />
                <Typography
                  variant="subtitle2"
                  fontWeight={config.font_weight_bold}
                  fontFamily={config.font_family}
                >
                  {lang?.creditsSpent || "Credits Spent"} (7d)
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="text.disabled"
                fontFamily={config.font_family}
              >
                {lang?.successfulTasksOnly || "Successful tasks only"}
              </Typography>

              {credit_chart.length > 0 ? (
                <MiniBarChart data={credit_chart} config={config} />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 64,
                    mt: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    fontFamily={config.font_family}
                  >
                    {lang?.noActivity || "No activity yet"}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Task breakdown */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: `${config.card.borderRadius}px`,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={config.font_weight_bold}
                fontFamily={config.font_family}
              >
                {lang?.taskBreakdown || "Task Breakdown"}
              </Typography>

              {task_breakdown.length === 0 ? (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  fontFamily={config.font_family}
                >
                  {lang?.noTasks || "No tasks yet"}
                </Typography>
              ) : (
                task_breakdown.map((t) => {
                  const totalCount = task_breakdown.reduce(
                    (s, x) => s + Number(x.count),
                    0,
                  );
                  const pct =
                    totalCount > 0 ? (Number(t.count) / totalCount) * 100 : 0;
                  return (
                    <Box key={t.task}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.4,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontFamily={config.font_family}
                          fontWeight={config.font_weight_medium}
                        >
                          {taskLabel(t.task)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontFamily={config.font_family}
                        >
                          {t.count} · {Number(t.total_credits).toLocaleString()}{" "}
                          cr
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        color="primary"
                        sx={{
                          height: 4,
                          borderRadius: 99,
                          "& .MuiLinearProgress-bar": { borderRadius: 99 },
                        }}
                      />
                    </Box>
                  );
                })
              )}
            </Box>
          </Grid>
        </Grid>

        {/* ── Recent Influencers + Usage Log ── */}
        <Grid container spacing={2}>
          {/* Recent influencers */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: `${config.card.borderRadius}px`,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={config.font_weight_bold}
                fontFamily={config.font_family}
                mb={2}
              >
                {lang?.recentInfluencers || "Recent Characters"}
              </Typography>

              {recent_influencers.length === 0 ? (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  fontFamily={config.font_family}
                >
                  {lang?.noInfluencers || "No characters yet"}
                </Typography>
              ) : (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {recent_influencers.map((inf, i) => (
                    <React.Fragment key={inf.id}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Avatar
                          src={
                            inf.photo_url ?? inf.photoUrl
                              ? mediaSrc(inf.photo_url ?? inf.photoUrl)
                              : undefined
                          }
                          sx={{
                            width: 38,
                            height: 38,
                            bgcolor: "primary.main",
                            fontSize: "0.85rem",
                          }}
                        >
                          {inf.name?.charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={config.font_weight_semibold}
                            fontFamily={config.font_family}
                            noWrap
                          >
                            {inf.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            fontFamily={config.font_family}
                          >
                            {moment(inf.created_at).fromNow()}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={inf.status}
                          color={
                            inf.status === "active" ? "success" : "default"
                          }
                          variant="outlined"
                          sx={{ fontSize: "0.62rem", height: 20 }}
                        />
                      </Box>
                      {i < recent_influencers.length - 1 && (
                        <Divider sx={{ opacity: 0.5 }} />
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Usage log */}
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: `${config.card.borderRadius}px`,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={config.font_weight_bold}
                fontFamily={config.font_family}
                mb={2}
              >
                {lang?.recentActivity || "Recent Activity"}
              </Typography>

              {recentActivity.length === 0 ? (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  fontFamily={config.font_family}
                >
                  {lang?.noActivity || "No activity yet"}
                </Typography>
              ) : (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}
                >
                  {recentActivity.map((log, i) => (
                    <React.Fragment key={`${log.task}-${log.createdAt}-${i}`}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                        }}
                      >
                        {/* Status icon */}
                        <Box sx={{ mt: 0.2, flexShrink: 0 }}>
                          {log.status === "success" ? (
                            <CheckCircleOutline
                              sx={{ fontSize: "1rem", color: "success.main" }}
                            />
                          ) : log.status === "pending" ? (
                            <AccessTimeOutlined
                              sx={{ fontSize: "1rem", color: "warning.main" }}
                            />
                          ) : (
                            <ErrorOutline
                              sx={{ fontSize: "1rem", color: "error.main" }}
                            />
                          )}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={config.font_weight_semibold}
                              fontFamily={config.font_family}
                              noWrap
                            >
                              {taskLabel(log.task)}
                            </Typography>
                            {log.credits != null && Number(log.credits) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.4,
                                  flexShrink: 0,
                                }}
                              >
                                <TokenOutlined
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "warning.main",
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  fontWeight={700}
                                  color="warning.main"
                                  fontFamily={config.font_family}
                                >
                                  {Number(log.credits).toLocaleString()}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            fontFamily={config.font_family}
                            noWrap
                            display="block"
                          >
                            {log.des}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            fontFamily={config.font_family}
                            sx={{ opacity: 0.6 }}
                          >
                            {moment(
                              log.createdAt ?? log.created_at,
                            ).fromNow()}
                          </Typography>
                        </Box>
                      </Box>
                      {i < recentActivity.length - 1 && (
                        <Divider sx={{ opacity: 0.4 }} />
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Dash;
